import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormulaLoader_WelcomeBack,
  FormulaLoader_LoadingFormula,
  FormulaLoader_WorkingForYou,
  FormulaLoader_Ready,
  FormulaLoader_Line1,
  FormulaLoader_Line2,
  FormulaLoader_Line3,
} from '../../../Constants';

const CHAR_DELAY = 22;      // ms per character
const LINE_PAUSE = 350;     // ms pause after each line finishes before moving to next
const READY_DELAY = 1400;   // ms after last line before showing "Ready!"
const CACHE_MIN = 1500;     // ms minimum display time for cached version
const TypewriterLine = ({ text, started, done }) => {
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    if (!started) return;
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, CHAR_DELAY);
    return () => clearInterval(interval);
  }, [started, text]);

  if (!started) return null;

  const isTyping = displayed.length < text.length;

  return (
    <div className="t-flex t-items-center t-gap-2 t-font-sans t-text-[15px] t-text-navy">
      <span
        className="t-inline-block t-w-1.5 t-h-1.5 t-rounded-full t-flex-shrink-0"
        style={{ backgroundColor: done ? '#29a999' : '#103642', opacity: done ? 1 : 0.5 }}
      />
      <span>
        {displayed}
        {isTyping && <span className="t-animate-pulse">|</span>}
      </span>
      {done && (
        <span className="t-text-[13px]" style={{ color: '#29a999' }}>✓</span>
      )}
    </div>
  );
};

const FormulaLoader = ({ fromCache = false, dataResolved = false, name, onComplete, skipPending = false }) => {
  const { t } = useTranslation();

  const lines = [t(FormulaLoader_Line1), t(FormulaLoader_Line2), t(FormulaLoader_Line3)];
  const lineDurations = lines.map((l) => l.length * CHAR_DELAY + LINE_PAUSE);

  // 'pending' → waiting to know which branch
  // 'cache'   → show welcome back variant
  // 'typewriter' → committed to typewriter animation
  const [phase, setPhase] = useState(skipPending ? 'typewriter' : 'pending');
  const [currentLine, setCurrentLine] = useState(0);
  const [showReady, setShowReady] = useState(false);
  const dataResolvedRef = useRef(false);
  const readyDelayPassedRef = useRef(false);

  // Cache phase typewriter
  const [cacheDisplayed, setCacheDisplayed] = useState('');
  const [showCacheSubtitle, setShowCacheSubtitle] = useState(false);

  const cacheTitle = name
    ? `${t(FormulaLoader_WelcomeBack)}, ${name}!`
    : `${t(FormulaLoader_WelcomeBack)}!`;

  const title = name
    ? `${name}, ${t(FormulaLoader_WorkingForYou)}`
    : t(FormulaLoader_WorkingForYou);

  // Keep a ref in sync so the timeout can read the latest value
  useEffect(() => {
    dataResolvedRef.current = dataResolved;
  }, [dataResolved]);

  // When dataResolved arrives, always decide the phase (cache wins even over typewriter)
  useEffect(() => {
    if (!dataResolved) return;
    setPhase(fromCache ? 'cache' : 'typewriter');
  }, [dataResolved]);

  // TYPEWRITER_TIMEOUT removed — fresh quiz uses skipPending (starts in typewriter directly),
  // returning users stay in pending dots until dataResolved arrives.

  // Advance typewriter line by line
  useEffect(() => {
    if (phase !== 'typewriter' || currentLine >= lines.length) return;
    const timer = setTimeout(() => setCurrentLine((p) => p + 1), lineDurations[currentLine]);
    return () => clearTimeout(timer);
  }, [phase, currentLine]);

  // Show "Ready!" after last line: always wait READY_DELAY first (visual polish dot-pause),
  // then show if data is ready. Hard fallback at 30s as last-resort escape hatch only.
  useEffect(() => {
    if (phase !== 'typewriter' || currentLine < lines.length) return;
    readyDelayPassedRef.current = false;

    const delayTimer = setTimeout(() => {
      readyDelayPassedRef.current = true;
      if (dataResolvedRef.current) setShowReady(true);
    }, READY_DELAY);

    const fallbackTimer = setTimeout(() => {
      setShowReady(true);
    }, 30000);

    return () => {
      clearTimeout(delayTimer);
      clearTimeout(fallbackTimer);
    };
  }, [phase, currentLine]);

  // When data arrives after the delay has already passed, show Ready immediately
  useEffect(() => {
    if (!dataResolved || phase !== 'typewriter' || currentLine < lines.length) return;
    if (readyDelayPassedRef.current) setShowReady(true);
  }, [dataResolved, phase, currentLine]);

  // Cache phase: dismiss after minimum display time
  useEffect(() => {
    if (phase !== 'cache') return;
    const timer = setTimeout(() => onComplete?.(), CACHE_MIN);
    return () => clearTimeout(timer);
  }, [phase]);

  // Typewriter phase: dismiss shortly after Ready! appears so user can read it
  useEffect(() => {
    if (!showReady) return;
    const timer = setTimeout(() => {
      onComplete?.();
    }, 800);
    return () => clearTimeout(timer);
  }, [showReady]);

  // Cache phase: type out the welcome back title, then show subtitle
  useEffect(() => {
    if (phase !== 'cache') return;
    setCacheDisplayed('');
    setShowCacheSubtitle(false);
    const fullText = cacheTitle;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setCacheDisplayed(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(interval);
        setTimeout(() => setShowCacheSubtitle(true), 300);
      }
    }, CHAR_DELAY);
    return () => clearInterval(interval);
  }, [phase, cacheTitle]);

  // --- PENDING: neutral state while waiting (no title — we don't know which variant yet) ---
  const fadeInStyle = <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>;

  if (phase === 'pending') {
    return (
      <div className="t-fixed t-inset-0 t-z-50 t-bg-white t-flex t-flex-col t-items-center t-justify-center t-gap-3 t-px-8">
        <div className="t-flex t-gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="t-inline-block t-w-1.5 t-h-1.5 t-rounded-full t-bg-navy/40 t-animate-bounce"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // --- CACHE: welcome back variant ---
  if (phase === 'cache') {
    const isCacheTyping = cacheDisplayed.length < cacheTitle.length;
    return (
      <div className="t-fixed t-inset-0 t-z-50 t-bg-white t-flex t-flex-col t-items-center t-justify-start t-pt-16 t-gap-3 t-px-8 md:t-justify-center md:t-pt-0">
        {fadeInStyle}
        <p className="!t-m-0 t-font-serif t-font-normal t-text-navy t-text-[28px] t-text-center">
          {cacheDisplayed}
          {isCacheTyping && <span className="t-animate-pulse">|</span>}
        </p>
        {showCacheSubtitle && (
          <p className="!t-m-0 t-font-sans t-text-[14px] t-text-navy/60 t-text-center" style={{ animation: 'fadeIn 0.3s ease' }}>
            {t(FormulaLoader_LoadingFormula)}
          </p>
        )}
        {showCacheSubtitle && (
          <div className="t-flex t-gap-1.5 t-mt-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="t-inline-block t-w-1.5 t-h-1.5 t-rounded-full t-bg-navy/40 t-animate-bounce"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- TYPEWRITER ---
  return (
    <div className="t-fixed t-inset-0 t-z-50 t-bg-white t-flex t-flex-col t-items-center t-justify-start t-pt-16 t-gap-8 t-px-8 md:t-justify-center md:t-pt-0">
      {fadeInStyle}
      <div className="t-flex t-flex-col t-items-center t-gap-2">
        <p className="!t-m-0 t-font-serif t-font-normal t-text-navy t-text-[28px] t-text-center">
          {title}
        </p>
        <div className="t-w-10 t-h-px t-bg-navy/20" />
      </div>

      <div className="t-flex t-flex-col t-gap-3 t-w-full t-max-w-xs">
        {lines.map((line, i) => (
          <TypewriterLine
            key={i}
            text={line}
            started={i <= currentLine}
            done={i < currentLine}
          />
        ))}
      </div>

      <div className="t-flex t-flex-col t-items-center t-gap-3">
        {!showReady && (
          <div className="t-flex t-gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="t-inline-block t-w-1.5 t-h-1.5 t-rounded-full t-bg-navy/40 t-animate-bounce"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
        )}
        {showReady && (
          <p className="!t-m-0 t-font-serif t-font-normal t-text-navy t-text-[22px]" style={{ animation: 'fadeIn 0.4s ease' }}>
            {t(FormulaLoader_Ready)}
          </p>
        )}
      </div>
    </div>
  );
};

export default FormulaLoader;
